/**
 * Procedural particle atlas (canvas 2D, generated once per renderer; no downloaded assets).
 * 4×4 cells of white shapes with alpha; particles tint them in the shader.
 */
import * as THREE from 'three';

export const ATLAS_COLS = 4;

export const FRAME = {
  glow: 0,
  core: 1,
  spark: 2,
  smoke: 3,
  star: 4,
  ring: 5,
  ember: 6,
  shard: 7,
  rune: 8,
  flame: 9,
  debris: 10,
  wisp: 11,
  crescent: 12,
  cross: 13,
  dust: 14,
  bolt: 15,
} as const;

const CELL = 128;

function rng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 10000) / 10000;
  };
}

type Ctx = CanvasRenderingContext2D;

function radial(g: Ctx, cx: number, cy: number, r: number, stops: [number, number][]): void {
  const grad = g.createRadialGradient(cx, cy, 0, cx, cy, r);
  for (const [o, a] of stops) grad.addColorStop(o, `rgba(255,255,255,${a})`);
  g.fillStyle = grad;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.fill();
}

const DRAW: Record<number, (g: Ctx, r: () => number) => void> = {
  [FRAME.glow]: (g) => radial(g, 64, 64, 60, [[0, 1], [0.2, 0.75], [0.45, 0.32], [0.7, 0.1], [1, 0]]),
  [FRAME.core]: (g) => radial(g, 64, 64, 60, [[0, 1], [0.18, 0.95], [0.35, 0.45], [0.6, 0.1], [1, 0]]),
  [FRAME.spark]: (g) => {
    g.save();
    g.translate(64, 64);
    g.scale(0.16, 1);
    radial(g, 0, 0, 60, [[0, 1], [0.3, 0.8], [0.7, 0.15], [1, 0]]);
    g.restore();
  },
  [FRAME.smoke]: (g, r) => {
    for (let i = 0; i < 9; i++) {
      const a = r() * Math.PI * 2;
      const d = r() * 22;
      radial(g, 64 + Math.cos(a) * d, 64 + Math.sin(a) * d, 26 + r() * 18, [[0, 0.32], [0.6, 0.14], [1, 0]]);
    }
  },
  [FRAME.star]: (g) => {
    g.save();
    g.translate(64, 64);
    for (const rot of [0, Math.PI / 2]) {
      g.save();
      g.rotate(rot);
      g.scale(0.07, 1);
      radial(g, 0, 0, 62, [[0, 1], [0.4, 0.5], [1, 0]]);
      g.restore();
    }
    g.restore();
    radial(g, 64, 64, 22, [[0, 1], [0.5, 0.4], [1, 0]]);
  },
  [FRAME.ring]: (g) => {
    const grad = g.createRadialGradient(64, 64, 0, 64, 64, 60);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.68, 'rgba(255,255,255,0)');
    grad.addColorStop(0.84, 'rgba(255,255,255,1)');
    grad.addColorStop(0.92, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, CELL, CELL);
  },
  [FRAME.ember]: (g) => radial(g, 64, 64, 40, [[0, 1], [0.25, 0.9], [0.5, 0.25], [1, 0]]),
  [FRAME.shard]: (g) => {
    const grad = g.createLinearGradient(64, 8, 64, 120);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(1, 'rgba(255,255,255,0.35)');
    g.fillStyle = grad;
    g.beginPath();
    g.moveTo(64, 6);
    g.lineTo(80, 70);
    g.lineTo(64, 122);
    g.lineTo(50, 64);
    g.closePath();
    g.fill();
  },
  [FRAME.rune]: (g) => {
    g.strokeStyle = 'rgba(255,255,255,0.95)';
    g.lineCap = 'round';
    g.lineWidth = 7;
    g.beginPath();
    g.arc(64, 64, 44, 0, Math.PI * 2);
    g.stroke();
    g.lineWidth = 6;
    g.beginPath();
    for (let i = 0; i < 3; i++) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 3;
      const b = a + (Math.PI * 2) / 3;
      g.moveTo(64 + Math.cos(a) * 44, 64 + Math.sin(a) * 44);
      g.lineTo(64 + Math.cos(b) * 44, 64 + Math.sin(b) * 44);
    }
    g.stroke();
    radial(g, 64, 64, 18, [[0, 0.9], [1, 0]]);
  },
  [FRAME.flame]: (g) => {
    const grad = g.createRadialGradient(64, 84, 2, 64, 84, 44);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.5, 'rgba(255,255,255,0.65)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.beginPath();
    g.moveTo(64, 8);
    g.bezierCurveTo(78, 40, 104, 70, 92, 98);
    g.bezierCurveTo(84, 120, 44, 120, 36, 98);
    g.bezierCurveTo(24, 70, 50, 40, 64, 8);
    g.fill();
  },
  [FRAME.debris]: (g, r) => {
    const n = 7;
    g.beginPath();
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const d = 30 + r() * 20;
      const x = 64 + Math.cos(a) * d;
      const y = 64 + Math.sin(a) * d;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.closePath();
    const grad = g.createLinearGradient(30, 30, 100, 100);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.55, 'rgba(170,170,170,1)');
    grad.addColorStop(1, 'rgba(90,90,90,1)');
    g.fillStyle = grad;
    g.fill();
  },
  [FRAME.wisp]: (g, r) => {
    for (let i = 0; i < 6; i++) {
      g.save();
      g.translate(64 + (r() - 0.5) * 18, 30 + i * 14);
      g.scale(0.5, 1);
      radial(g, 0, 0, 26, [[0, 0.35], [1, 0]]);
      g.restore();
    }
  },
  [FRAME.crescent]: (g) => {
    g.fillStyle = 'rgba(255,255,255,1)';
    g.beginPath();
    g.arc(64, 64, 54, Math.PI * 0.15, Math.PI * 0.85, false);
    g.arc(64, 44, 44, Math.PI * 0.8, Math.PI * 0.2, true);
    g.closePath();
    g.fill();
  },
  [FRAME.cross]: (g) => {
    g.save();
    g.translate(64, 64);
    for (const rot of [0, Math.PI / 2, Math.PI / 4, -Math.PI / 4]) {
      g.save();
      g.rotate(rot);
      g.scale(rot === 0 || rot === Math.PI / 2 ? 0.1 : 0.06, rot === 0 || rot === Math.PI / 2 ? 1 : 0.55);
      radial(g, 0, 0, 60, [[0, 1], [0.5, 0.45], [1, 0]]);
      g.restore();
    }
    g.restore();
  },
  [FRAME.dust]: (g, r) => {
    for (let i = 0; i < 14; i++) {
      const a = r() * Math.PI * 2;
      const d = r() * 30;
      radial(g, 64 + Math.cos(a) * d, 64 + Math.sin(a) * d, 16 + r() * 20, [[0, 0.2], [1, 0]]);
    }
  },
  [FRAME.bolt]: (g, r) => {
    g.strokeStyle = 'rgba(255,255,255,1)';
    g.lineWidth = 5;
    g.lineJoin = 'miter';
    g.beginPath();
    let x = 64;
    g.moveTo(x, 6);
    for (let y = 20; y <= 122; y += 14) {
      x = 64 + (r() - 0.5) * 50;
      g.lineTo(x, y);
    }
    g.stroke();
  },
};

/** Build the atlas texture (call once; dispose with the renderer). */
export function createFxAtlas(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = CELL * ATLAS_COLS;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, c.width, c.height);
  for (let f = 0; f < ATLAS_COLS * ATLAS_COLS; f++) {
    const draw = DRAW[f];
    if (!draw) continue;
    const cx = (f % ATLAS_COLS) * CELL;
    // Row 0 at the top of the canvas = v near 1 (flipY), the shader accounts for it.
    const cy = Math.floor(f / ATLAS_COLS) * CELL;
    g.save();
    g.beginPath();
    g.rect(cx + 2, cy + 2, CELL - 4, CELL - 4);
    g.clip();
    g.translate(cx, cy);
    draw(g, rng(f * 7919 + 13));
    g.restore();
  }
  const t = new THREE.CanvasTexture(c);
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  return t;
}
